// MOB-6535 turn off this logging in our app so we can have Log Rocket was enabled
// import { zLogRocket } from '@zoll/logrocket-react-lib'

import { combineReducers, createStore, applyMiddleware, Store, compose } from 'redux'
import { composeWithDevTools } from 'redux-devtools-extension'
import createSagaMiddleware from 'redux-saga'

import { exceptionHandlerMiddleware, reducerLoggerMiddleware } from './middlewares'
import { StoreConstants } from './common/storeConstants'

import { initSagas } from './sagas'

import { initialUserState, userReducer } from './features/user/store/user.reducers'
import { IUserState } from './features/user/store/user.types'

import { initialTimelineState, timelineReducer } from './features/timeline/store/timeline.reducers'
import { ITimelineState } from './features/timeline/store/timeline.types'

import { demoReducer, initialDemoState } from './features/demo/store/demo.reducers'
import { IDemoState } from './features/demo/store/demo.types'

import { homeReducer, initialHomeState } from './features/home/store/home.reducers'
import { IHomeState } from './features/home/store/home.types'

import { initialVitalsState, vitalsReducer } from './features/vitals/store/vitals.reducers'
import { IPatientVitals } from './features/vitals/store/vitals.types'

import { initialLoginState, loginReducer } from './features/login/store/login.reducers'
import { ILoginState } from './features/login/store/login.types'

import { alertReducer, initialAlertState } from './components/alert/store/alert.reducer'
import { IAlertState } from './components/alert/store/alert.types'
import { toastReducer, initialToastState } from './components/toast/store/toast.reducer'
import { IToastState } from './components/toast/store/toast.types'
import { sessionResumeReducer, initialSessionResumeState } from './features/session/resume/store/sessionResume.reducers'
import { ISessionResumeState } from './features/session/resume/store/sessionResume.types'
import {
  sessionPersistReducer,
  initialSessionPersistState,
} from './features/session/persist/store/sessionPersist.reducers'
import { ISessionPersistState } from './features/session/persist/store/sessionPersist.types'

import { initialPCRState, pcrDbReducer } from './features/pcr/store/pcr.reducers'
import { IPCRState } from './features/pcr/store/pcr.types'

import { cookieReducer, initialCookieState } from './features/cookie/store/cookie.reducers'
import { ICookieState } from './features/cookie/store/cookie.types'

import { ICrewState } from './features/crew/store/crew.types'
import { crewReducer, initialCrewState } from './features/crew/store/crew.reducers'

import { ITimesState } from './features/times/store/times.types'
import { timesReducer, initialTimesState } from './features/times/store/times.reducers'

import { IScrollToTopState } from './components/scrollToTopButton/store/scrollToTop.types'
import { initialScrollToTopState, scrollToTopReducer } from './components/scrollToTopButton/store/scrollToTop.reducer'

import { IServiceState } from './features/serviceSelection/store/serviceSelection.types'
import { initialServiceState, serviceReducer } from './features/serviceSelection/store/serviceSelection.reducer'

import { ICardiacArrestState } from './features/quickActions/cardiacArrest/store/cardiacArrest.types'
import {
  cardiacArrestReducer,
  initialCardiacArrestState,
} from './features/quickActions/cardiacArrest/store/cardiacArrest.reducers'

import { IPatientDemographicsState } from './features/patientDemographics/store/patientDemographics.types'
import {
  patientDemographicsReducer,
  initialPatientDemographicsState,
} from './features/patientDemographics/store/patientDemographics.reducer'

import { IValidationState } from './features/validation/store/validation.types'
import { initialValidationState, validationReducer } from './features/validation/store/validation.reducers'

import {
  initialDriversLicenseScannerState,
  driversLicenseScannerReducer,
} from './features/driversLicenseScanner/store/driversLicenseScanner.reducers'
import { IDriversLicenseScannerState } from './features/driversLicenseScanner/store/driversLicenseScanner.types'

import { IPatientAllergiesState } from './features/patientAllergies/store/patientAllergies.types'
import {
  initialPatientAllergiesState,
  patientAllergiesReducer,
} from './features/patientAllergies/store/patientAllergies.reducers'

import { IPatientInformationState } from './features/patientInformation/store/patientInformation.types'
import {
  initialPatientInformationState,
  PatientInformationReducer,
} from './features/patientInformation/store/patientInformation.reducers'

import { initialSearchState, searchReducer } from './components/search/store/search.reducer'
import { ISearchState } from './components/search/store/search.types'

import { IPatientMedicalHistoryState } from './features/patientMedicalHistory/store/patientMedicalHistory.types'
import {
  initialPatientMedicalHistoryState,
  patientMedicalHistoryReducer,
} from './features/patientMedicalHistory/store/patientMedicalHistory.reducers'

import { IPatientMedicationsState } from './features/patientMedications/store/patientMedications.types'
import {
  initialPatientMedicationsState,
  patientMedicationsReducer,
} from './features/patientMedications/store/patientMedications.reducers'

import { ISingleSelectPageState } from './components/singleSelectPage/store/singleSelectPage.types'
import {
  initialSingleSelectPageState,
  singleSelectPageReducer,
} from './components/singleSelectPage/store/singleSelectPage.reducers'

import { ICardiacState } from './features/procedures/cardiac/store/cardiac.types'
import { initialCardiacState, cardiacReducer } from './features/procedures/cardiac/store/cardiac.reducers'

import { IAddMedicationAdministeredState } from './features/addMedicationAdministered/store/addMedicationAdministered.types'
import {
  addMedicationAdministeredReducer,
  initialAddMedicationAdministeredState,
} from './features/addMedicationAdministered/store/addMedicationAdministered.reducers'

import { initialQuickActionsState, quickActionsReducer } from './features/quickActions/store/quickActions.reducers'
import { IQuickActionsState } from './features/quickActions/store/quickActions.types'

import { IProcedureState } from './components/procedure/store/procedure.types'
import { initialProcedureState, procedureReducer } from './components/procedure/store/procedure.reducers'

import { IIntubationState } from './features/intubation/store/intubation.types'
import { initialIntubationState, intubationReducer } from './features/intubation/store/intubation.reducers'

import { initialOptionsState, optionsReducer } from './features/options/store/options.reducers'
import { IOptionsState } from './features/options/store/options.types'

import { ITripInformationState } from './features/tripInformation/store/tripInformation.types'
import {
  initialTripInformationState,
  tripInformationReducer,
} from './features/tripInformation/store/tripInformation.reducers'

import { IPickupAddressState } from './features/pickupAddress/store/pickupAddress.types'
import { initialPickupAddressState, pickupAddressReducer } from './features/pickupAddress/store/pickupAddress.reducers'

import { IAirwayState } from './features/procedures/airway/store/airway.types'
import { airwayReducer, initialAirwayState } from './features/procedures/airway/store/airway.reducers'

import { IImagingUltrasoundState } from './features/procedures/imagingUltrasound/store/ImagingUltrasound.types'
import {
  imagingUltrasoundReducer,
  initialImagingUltrasoundState,
} from './features/procedures/imagingUltrasound/store/ImagingUltrasound.reducers'

import { IChildbirthOBState } from './features/procedures/childbirthOB/store/childbirthOB.types'
import {
  childbirthOBReducer,
  initialChildbirthOBState,
} from './features/procedures/childbirthOB/store/childbirthOB.reducers'

import { IInitiateIvState } from './features/procedures/initiateIv/store/initiateIv.types'
import { initialInitiateIvState, initiateIvReducer } from './features/procedures/initiateIv/store/initiateIv.reducers'

import { IImmobilizationState } from './features/procedures/immobilization/store/immobilization.types'
import {
  immobilizationReducer,
  initialImmobilizationState,
} from './features/procedures/immobilization/store/immobilization.reducers'
import { IMedicalConsultState } from './features/procedures/medicalConsult/store/medicalConsult.types'
import {
  initialMedicalConsultState,
  medicalConsultReducer,
} from './features/procedures/medicalConsult/store/medicalConsult.reducers'
import { IExtricationState } from './features/procedures/extrication/store/extrication.types'
import {
  extricationReducer,
  initialExtricationState,
} from './features/procedures/extrication/store/extrication.reducers'

import { IOperationsState } from './features/procedures/operations/store/operations.types'
import { initialOperationsState, operationsReducer } from './features/procedures/operations/store/operations.reducers'

import { IRescueState } from './features/procedures/rescue/store/rescue.types'
import { initialRescueState, rescueReducer } from './features/procedures/rescue/store/rescue.reducers'

import { IVentilatorState } from './features/procedures/ventilator/store/ventilator.types'
import { initialVentilatorState, ventilatorReducer } from './features/procedures/ventilator/store/ventilator.reducers'

import { IHospitalNotifyState } from './features/procedures/hospitalNotify/store/hospitalNotify.types'
import {
  hospitalNotifyReducer,
  initialHospitalNotifyState,
} from './features/procedures/hospitalNotify/store/hospitalNotify.reducers'

import { IWoundCareState } from './features/procedures/woundCare/store/woundCare.types'
import { initialWoundCareState, woundCareReducer } from './features/procedures/woundCare/store/woundCare.reducers'

import { ISubmitChartState } from './features/submitChart/store/submitChart.types'
import { initialSubmitChartState, submitChartReducer } from './features/submitChart/store/submitChart.reducers'

import { ISignaturesState } from './features/signatures/store/signatures.types'
import { initialSignaturesState, signaturesReducer } from './features/signatures/store/signatures.reducer'

import { INarrativeState } from './features/narrative/store/narrative.types'
import { initialNarrativeState, narrativeReducer } from './features/narrative/store/narrative.reducers'

import { ICadImportState } from './features/cadImport/store/cadImport.types'
import { cadImportReducer, initialCadImportState } from './features/cadImport/store/cadImport.reducers'

import {
  IHistoryState,
  ISituationState,
  ICcHpiCardiacArrestState,
  ICcHpiInjuryDetailsState,
} from './features/ccHpi/store/ccHpi.types'
import {
  historyReducer,
  initialHistoryState,
  initialSituationState,
  situationReducer,
  ccHpiCardiacArrestReducer,
  initialCcHpiCardiacArrestState,
  ccHpiInjuryDetailsReducer,
  initialCcHpiInjuryDetailsState,
} from './features/ccHpi/store/ccHpi.reducers'

import { IFluidsState } from './features/fluids/store/fluids.types'
import { fluidsReducer, initialFluidsState } from './features/fluids/store/fluids.reducers'

import { initialZipState, zipReducer } from './components/zip/store/zip.reducers'
import { IZipState } from './components/zip/store/zip.types'

import { IDispositionState } from './features/destinationAddress/store/destinationAddress.types'
import {
  destinationAddressReducer,
  initialDispostionState,
} from './features/destinationAddress/store/destinationAddress.reducers'

import { IConfigurationsState } from './features/configurations/store/configurations.types'
import {
  configurationsReducer,
  initialConfigurationsState,
} from './features/configurations/store/configurations.reducers'
import { ICmsInformationState } from './features/cmsInformation/store/cmsInformation.types'
import {
  cmsInformationReducer,
  initialCmsInformationState,
} from './features/cmsInformation/store/cmsInformation.reducers'

import { IBillingState } from './features/billing/store/billing.types'
import { initialBillingState, BillingReducer } from './features/billing/store/billing.reducers'
import { IRelationshipsAndGuarantorsState } from './features/relationshipsAndGuarantors/store/relationshipsAndGuarantors.types'
import {
  initialRelationshipsAndGuarantorsState,
  relationshipsAndGuarantorsReducer,
} from './features/relationshipsAndGuarantors/store/relationshipsAndGuarantors.reducers'
import {
  initialPcsInformationState,
  pcsInformationReducer,
} from './features/pcsInformation/store/pcsInformation.reducers'
import { IPcsInformationState } from './features/pcsInformation/store/pcsInformation.types'
import {
  initialSpecialReportsState,
  specialReportsReducer,
} from './features/specialReports/store/specialReports.reducers'
import { ISpecialReportsState } from './features/specialReports/store/specialReports.types'
import { IAuthorizationInformationState } from './features/authorizationInformation/store/authorizationInformation.types'
import {
  authorizationInformationReducer,
  initialAuthorizationInformationState,
} from './features/authorizationInformation/store/authorizationInformation.reducers'

import { IInsuranceInformationState } from './features/insuranceInformation/store/insuranceInformation.types'
import {
  initialInsuranceInformationState,
  insuranceInformationReducer,
} from './features/insuranceInformation/store/insuranceInformation.reducers'
import { IPaymentInformationState } from './features/paymentInformation/store/paymentInformation.types'
import {
  initialPaymentInformationState,
  paymentInformationReducer,
} from './features/paymentInformation/store/paymentInformation.reducers'

import { IPatientSearchState } from './features/patientSearch/store/patientSearch.types'
import { initialPatientSearchState, patientSearchReducer } from './features/patientSearch/store/patientSearch.reducers'

import { IApiErrorHandlerState } from './components/apiErrorHandler/store/apiErrorHandler.types'
import {
  apiErrorHandlerReducer,
  initialApiErrorHandlerState,
} from './components/apiErrorHandler/store/apiErrorHandler.reducer'

import { IPatientImmunizationsState } from './features/patientImmunizations/store/patientImmunizations.types'
import {
  initialPatientImmunizationsState,
  patientImmunizationsReducer,
} from './features/patientImmunizations/store/patientImmunizations.reducers'

import { IClientDataState } from './features/clientData/store/clientData.types'
import { clientDataReducer, initialclientDataState } from './features/clientData/store/clientData.reducers'

import { ICustomFormsSignatureState } from './features/customSignature/store/customSignature.types'
import {
  customFormsSignatureReducer,
  initialCustomFormsState,
} from './features/customSignature/store/customSignature.reducer'
import { ISceneDescriptionState } from './features/sceneDescription/store/sceneDescription.types'
import {
  initialSceneDescriptionState,
  sceneDescriptionReducer,
} from './features/sceneDescription/store/sceneDescription.reducers'

import { ICameraAttachmentsState } from './features/cameraAttachments/store/cameraAttachments.types'
import {
  cameraAttachmentsReducer,
  initialCameraAttachmentsState,
} from './features/cameraAttachments/store/cameraAttachments.reducer'

import { IFactorsAffectingCareState } from './features/factorsAffectingCare/store/factorsAffectingCare.types'
import {
  factorsAffectingCareReducer,
  initialFactorsAffectingCareState,
} from './features/factorsAffectingCare/store/factorsAffectingCare.reducers'
import { IZollSupportTicketState } from './features/zollSupportTicket/store/zollSupportTicket.types'
import {
  initialZollSupportTicketState,
  zollSupportTicketReducer,
} from './features/zollSupportTicket/store/zollSupportTicket.reducers'
import { IInitialAssessmentState } from './features/initialAssessments/store/initialAssessment.types'
import {
  initialAssessmentReducer,
  initialAssessmentState,
} from './features/initialAssessments/store/initialAssessment.reducers'
import {
  initialAssessmentsAirwayState,
  initialAssessmentsAirwayReducer,
} from './features/initialAssessments/airway/store/airway.reducers'
import { IAssessmentsAirwayState } from './features/initialAssessments/airway/store/airway.types'
import { IRespiratoryState } from './features/initialAssessments/respiratory/store/respiratory.types'
import {
  RespiratoryReducer,
  respiratoryState,
} from './features/initialAssessments/respiratory/store/respiratory.reducers'
import { IWasPatientImmobilizedState } from './features/initialAssessments/neuro/wasPatientImmobilized/store/wasPatientImmobilized.types'
import {
  initialWasPatientImmobilizedState,
  wasPatientImmobilizedReducer,
} from './features/initialAssessments/neuro/wasPatientImmobilized/store/wasPatientImmobilized.reducers'
import { IGlassgowCommaScoreState } from './features/initialAssessments/neuro/glassgowCommaScore/store/glassgowCommaScore.types'
import {
  glassgowCommaScoreReducer,
  initialGlassgowCommaScoreState,
} from './features/initialAssessments/neuro/glassgowCommaScore/store/glassgowCommaScore.reducers'
import { IPediatricTraumaScoreState } from './features/initialAssessments/neuro/pediatricTraumaScore/store/pediatricTraumaScore.types'
import {
  initialPediatricTraumaScoreState,
  pediatricTraumaScoreReducer,
} from './features/initialAssessments/neuro/pediatricTraumaScore/store/pediatricTraumaScore.reducers'
import { IRevisedTraumaScoreState } from './features/initialAssessments/neuro/revisedTraumaScore/store/revisedTraumaScore.types'
import {
  initialRevisedTraumaScoreState,
  revisedTraumaScoreReducer,
} from './features/initialAssessments/neuro/revisedTraumaScore/store/revisedTraumaScore.reducers'
import { INeuroState } from './features/initialAssessments/neuro/store/neuro.types'
import { neuroReducer, initialNeuroState } from './features/initialAssessments/neuro/store/neuro.reducers'
import { IMotorSensoryAssessmentState } from './features/initialAssessments/neuro/motorSensoryAssessment/store/motoSensoryAssessment.types'
import {
  initialMotorSensoryAssessmentState,
  motorSensoryAssessmentReducer,
} from './features/initialAssessments/neuro/motorSensoryAssessment/store/motoSensoryAssessment.reducers'
import { IPupilsState } from './features/initialAssessments/neuro/pupils/store/pupils.types'
import { pupilsReducer, initialPupilsState } from './features/initialAssessments/neuro/pupils/store/pupils.reducers'
import { initialPulsesState, pulsesReducer } from './features/initialAssessments/cardio/pulses/store/pulses.reducer'
import { IPulsesState } from './features/initialAssessments/cardio/pulses/store/pulses.types'
import { ICardioState } from './features/initialAssessments/cardio/store/cardio.types'
import { cardioReducer, initialCardioState } from './features/initialAssessments/cardio/store/cardio.reducers'
import { drainReducer, initialDrainState } from './features/procedures/drain/store/drain.reducers'
import { IDrainState } from './features/procedures/drain/store/drain.types'
import { ITitrateState } from './features/procedures/titrate/store/titrate.types'
import { initialTitrateState, titrateReducer } from './features/procedures/titrate/store/titrate.reducers'
import { ILabsState } from './features/procedures/labs/store/labs.types'
import { initialLabsState, labsReducer } from './features/procedures/labs/store/labs.reducer'
import { IPnProcedureState } from './features/procedures/pnProcedure/store/pnProcedure.types'
import {
  pnProcedureReducer,
  initialPnProcedureState,
} from './features/procedures/pnProcedure/store/pnProcedure.reducers'

import { IPnMedicationState } from './features/procedures/pnMedication/store/pnMedication.types'
import {
  pnMedicationReducer,
  initialPnMedicationState,
} from './features/procedures/pnMedication/store/pnMedication.reducers'
import { IRulesState } from './features/rules/store/rules.types'
import { initialRulesState, rulesReducer } from './features/rules/store/rules.reducers'
import { IExposureState } from './features/exposure/store/exposure.types'
import { exposureReducer, initialExposureState } from './features/exposure/store/exposure.reducers'
import { employerReducer, initialEmployerState } from './features/patientDemographics/employer/store/employer.reducers'
import { IEmployerState } from './features/patientDemographics/employer/store/employer.types'
import { IMotorVehicleIncidentState } from './features/ccHpi/motorVehicleIncident/store/motorVehicleIncident.types'
import {
  initialMotorVehicleIncidentState,
  motorVehicleIncidentReducer,
} from './features/ccHpi/motorVehicleIncident/store/motorVehicleIncident.reducers'
import { ekgImportReducer, initialEKGImportState } from './features/ekgImport/store/ekgImport.reducers'
import { IEKGImportState } from './features/ekgImport/store/ekgImport.types'
import { pulsaraReducer, initialPulsaraState } from './features/pulsara/store/pulsara.reducers'
import { IPulsaraState } from './features/pulsara/store/pulsara.types'
import { qaAddendumReducer, initialQaAddendumState } from './features/qaFlags/qaAddendums/store/qaAddendum.reducers'
import { IQaAddendumState } from './features/qaFlags/qaAddendums/store/qaAddendum.types'
import {
  notificationCenterReducer,
  initialNotificationCenterState,
} from './features/notificationCenter/store/notificationCenter.reducers'
import { INotificationCenterState } from './features/notificationCenter/store/notificationCenter.types'
import { rulesMiddleware } from './features/rules/middleware/rules.middleware'
import { IDocumentScannerState } from './features/documentScanner/store/documentScanner.types'
import {
  documentScannerReducer,
  initialDocumentScannerState,
} from './features/documentScanner/store/documentScanner.reducer'

import { IDiagnosisState } from './features/diagnosis/store/diagnosis.types'
import { diagnosisReducer, initialDiagnosisState } from './features/diagnosis/store/diagnosis.reducers'
import { ICommentsState } from './components/commentSection/store/comments.types'
import { commentsReducer, initialCommentsState } from './components/commentSection/store/comments.reducers'
import { IAdditionalAssessmentsState } from './features/additionalAssessments/store/additionalAssessments.types'
import {
  initialAdditionalAssessmentsState,
  additionalAssessmentsReducer,
} from './features/additionalAssessments/store/additionalAssessments.reducers'
import { IBodyAssessmentState } from './features/additionalAssessments/bodyAssessment/store/bodyAssessment.types'
import {
  initialBodyAssessmentState,
  bodyAssessmentReducer,
} from './features/additionalAssessments/bodyAssessment/store/bodyAssessment.reducers'
import { IDrainsAndTubesState } from './features/additionalAssessments/bodyAssessment/drainsTubes/store/drainsAndTubes.types'
import {
  drainsAndTubesReducer,
  initialDrainsAndTubesState,
} from './features/additionalAssessments/bodyAssessment/drainsTubes/store/drainsAndTubes.reducers'
import { IObstetricsState } from './features/additionalAssessments/bodyAssessment/obstetrics/store/obstetrics.types'
import {
  initialObstetricsState,
  obstetricsReducer,
} from './features/additionalAssessments/bodyAssessment/obstetrics/store/obstetrics.reducers'
import { IBurnsState } from './features/additionalAssessments/bodyAssessment/burns/store/burns.types'
import {
  burnsReducer,
  initialBurnsState,
} from './features/additionalAssessments/bodyAssessment/burns/store/burns.reducers'
import { IBodySurveyState } from './features/additionalAssessments/bodyAssessment/bodySurvey/store/bodySurvey.types'
import {
  bodySurveyReducer,
  initialBodySurveyState,
} from './features/additionalAssessments/bodyAssessment/bodySurvey/store/bodySurvey.reducers'
import { IDefaultsState } from './features/defaults/store/defaults.types'
import { defaultsReducer, initialDefaultsState } from './features/defaults/store/defaults.reducer'
import { IPNNVState } from './components/pnnv/store/pnnv.types'
import { initialPNNVState, pnnvReducer } from './components/pnnv/store/pnnv.reducer'
import { ILocationSearchState } from './features/locationSearch/store/locationSearch.types'
import {
  initialLocationSearchState,
  locationSearchReducer,
} from './features/locationSearch/store/locationSearch.reducers'
import { IGlobalDataState } from './features/globalData/store/globalData.types'
import { globalDataReducer, initialGlobalDataState } from './features/globalData/store/globalData.reducers'
import { IStateCustomDataDisplayState } from './features/nemsisData/store/nemsisCustomData.types'
import {
  initialCustomDisplayDataState,
  stateCustomDisplayData,
} from './features/nemsisData/store/nemsisCustomData.reducers'
import { ICompleteChartState, ISignerState } from './features/completeChart/store/completeChart.types'
import {
  completeChartReducer,
  initialCompleteChartState,
  initialSignerState,
  signerReducer,
} from './features/completeChart/store/completeChart.reducers'
import { IBloodProductAdministrationState } from './features/procedures/bloodProductAdministration/store/bloodProductAdministration.types'
import {
  bloodProductAdministrationReducer,
  initialBloodProductAdministrationState,
} from './features/procedures/bloodProductAdministration/store/bloodProductAdministration.reducers'
import { IVentricularAssistDeviceState } from './features/procedures/ventricularAssistDevice/store/ventricularAssistDevice.types'
import {
  initialVentricularAssistDeviceState,
  ventricularAssistDeviceReducer,
} from './features/procedures/ventricularAssistDevice/store/ventricularAssistDevice.reducers'
import { ICriticalCareState } from './features/procedures/criticalCare/store/criticalCare.types'
import {
  criticalCareReducer,
  initialCriticalCareState,
} from './features/procedures/criticalCare/store/criticalCare.reducers'
import { IAssessmentState } from './features/procedures/assessment/store/assessment.types'
import {
  assessmentReducer,
  initialAssessmentProcedureState,
} from './features/procedures/assessment/store/assessment.reducers'
import { IFillablePDFsState } from './features/fillablePDFs/store/fillablePDFs.types'
import { fillablePDFsReducer, initialFillablePDFsState } from './features/fillablePDFs/store/fillablePDFs.reducers'
import { IPcrBackupState } from './features/pcrBackup/store/pcrBackup.types'
import { initialPcrBackupState, pcrBackupReducer } from './features/pcrBackup/store/pcrBackup.reducers'

import { ILoaderState } from './components/loader/store/loader.types'
import { initialLoaderState, loaderReducer } from './components/loader/store/loader.reducer'

export interface IRootState {
  user: IUserState
  timeline: ITimelineState
  demo: IDemoState
  home: IHomeState
  vitals: IPatientVitals
  login: ILoginState
  alert: IAlertState
  toast: IToastState
  sessionResume: ISessionResumeState
  loader: ILoaderState
  sessionPersist: ISessionPersistState
  pcr: IPCRState
  cookie: ICookieState
  crew: ICrewState
  times: ITimesState
  scrollToTop: IScrollToTopState
  cardiacArrest: ICardiacArrestState
  services: IServiceState
  patientInformation: IPatientInformationState
  patientDemographics: IPatientDemographicsState
  employer: IEmployerState
  patientAllergies: IPatientAllergiesState
  patientMedications: IPatientMedicationsState
  validation: IValidationState
  search: ISearchState
  dlScanner: IDriversLicenseScannerState
  patientMedicalHistory: IPatientMedicalHistoryState
  singleSelectPage: ISingleSelectPageState
  cardiac: ICardiacState
  addMedicationAdministered: IAddMedicationAdministeredState
  quickActions: IQuickActionsState
  procedure: IProcedureState
  intubation: IIntubationState
  options: IOptionsState
  tripInformation: ITripInformationState
  pickupAddress: IPickupAddressState
  airway: IAirwayState
  imagingUltrasound: IImagingUltrasoundState
  childbirthOB: IChildbirthOBState
  initiateIv: IInitiateIvState
  immobilization: IImmobilizationState
  medicalConsult: IMedicalConsultState
  extrication: IExtricationState
  operations: IOperationsState
  rescue: IRescueState
  ventilator: IVentilatorState
  criticalCare: ICriticalCareState
  hospitalNotify: IHospitalNotifyState
  woundCare: IWoundCareState
  submitChart: ISubmitChartState
  signatures: ISignaturesState
  narrative: INarrativeState
  cadImport: ICadImportState
  situation: ISituationState
  history: IHistoryState
  ccHpiCardiacArrest: ICcHpiCardiacArrestState
  ccHpiInjuryDetails: ICcHpiInjuryDetailsState
  zip: IZipState
  disposition: IDispositionState
  configurations: IConfigurationsState
  billing: IBillingState
  insuranceInformation: IInsuranceInformationState
  relationshipsAndGuarantors: IRelationshipsAndGuarantorsState
  pcsInformation: IPcsInformationState
  specialReports: ISpecialReportsState
  authorizationInformation: IAuthorizationInformationState
  cmsInformation: ICmsInformationState
  paymentInformation: IPaymentInformationState
  apiErrorHandler: IApiErrorHandlerState
  patientSearch: IPatientSearchState
  ekgImport: IEKGImportState
  pulsara: IPulsaraState
  qaAddendum: IQaAddendumState
  notificationCenter: INotificationCenterState
  patientImmunizations: IPatientImmunizationsState
  clientData: IClientDataState
  cameraAttachments: ICameraAttachmentsState
  customFormSignature: ICustomFormsSignatureState
  factorsAffectingCareState: IFactorsAffectingCareState
  sceneDescription: ISceneDescriptionState
  exposure: IExposureState
  titrate: ITitrateState
  zollSupportTicket: IZollSupportTicketState
  initialAssessment: IInitialAssessmentState
  initialAssessmentAirway: IAssessmentsAirwayState
  respiratory: IRespiratoryState
  neuro: INeuroState
  motorSensoryAssessment: IMotorSensoryAssessmentState
  pupils: IPupilsState
  wasPatientImmobilized: IWasPatientImmobilizedState
  glassgowCommaScore: IGlassgowCommaScoreState
  pediatricTraumaScore: IPediatricTraumaScoreState
  revisedTraumaScore: IRevisedTraumaScoreState
  pulses: IPulsesState
  initialAssessmentCardio: ICardioState
  drain: IDrainState
  labs: ILabsState
  pnProcedure: IPnProcedureState
  medicationAdministeredStateListPNNVs: IPnMedicationState
  motorVehicleIncident: IMotorVehicleIncidentState
  rules: IRulesState
  diagnosis: IDiagnosisState
  __comments: ICommentsState
  additionalAssessments: IAdditionalAssessmentsState
  bodyAssessment: IBodyAssessmentState
  drainsAndTubes: IDrainsAndTubesState
  obstetrics: IObstetricsState
  burns: IBurnsState
  bodySurvey: IBodySurveyState
  defaults: IDefaultsState
  pnnv: IPNNVState
  locationSearch: ILocationSearchState
  globalData: IGlobalDataState
  documentScanner: IDocumentScannerState
  customDisplayData: IStateCustomDataDisplayState
  completeChart: ICompleteChartState
  signerData: ISignerState
  bloodProductAdministration: IBloodProductAdministrationState
  ventricularAssistDevice: IVentricularAssistDeviceState
  assessment: IAssessmentState
  fluids: IFluidsState
  fillablePDFs: IFillablePDFsState
  pcrBackup: IPcrBackupState
}

export const rootReducer = combineReducers<IRootState>({
  user: userReducer,
  timeline: timelineReducer,
  demo: demoReducer,
  home: homeReducer,
  vitals: vitalsReducer,
  login: loginReducer,
  alert: alertReducer,
  toast: toastReducer,
  loader: loaderReducer,
  sessionResume: sessionResumeReducer,
  sessionPersist: sessionPersistReducer,
  pcr: pcrDbReducer,
  cookie: cookieReducer,
  crew: crewReducer,
  times: timesReducer,
  scrollToTop: scrollToTopReducer,
  cardiacArrest: cardiacArrestReducer,
  services: serviceReducer,
  patientInformation: PatientInformationReducer,
  patientDemographics: patientDemographicsReducer,
  employer: employerReducer,
  patientAllergies: patientAllergiesReducer,
  patientMedications: patientMedicationsReducer,
  validation: validationReducer,
  search: searchReducer,
  dlScanner: driversLicenseScannerReducer,
  patientMedicalHistory: patientMedicalHistoryReducer,
  singleSelectPage: singleSelectPageReducer,
  cardiac: cardiacReducer,
  addMedicationAdministered: addMedicationAdministeredReducer,
  quickActions: quickActionsReducer,
  procedure: procedureReducer,
  intubation: intubationReducer,
  options: optionsReducer,
  tripInformation: tripInformationReducer,
  pickupAddress: pickupAddressReducer,
  airway: airwayReducer,
  imagingUltrasound: imagingUltrasoundReducer,
  childbirthOB: childbirthOBReducer,
  initiateIv: initiateIvReducer,
  immobilization: immobilizationReducer,
  medicalConsult: medicalConsultReducer,
  extrication: extricationReducer,
  operations: operationsReducer,
  rescue: rescueReducer,
  ventilator: ventilatorReducer,
  criticalCare: criticalCareReducer,
  hospitalNotify: hospitalNotifyReducer,
  woundCare: woundCareReducer,
  submitChart: submitChartReducer,
  signatures: signaturesReducer,
  narrative: narrativeReducer,
  cadImport: cadImportReducer,
  situation: situationReducer,
  history: historyReducer,
  ccHpiCardiacArrest: ccHpiCardiacArrestReducer,
  ccHpiInjuryDetails: ccHpiInjuryDetailsReducer,
  fluids: fluidsReducer,
  zip: zipReducer,
  disposition: destinationAddressReducer,
  configurations: configurationsReducer,
  billing: BillingReducer,
  insuranceInformation: insuranceInformationReducer,
  relationshipsAndGuarantors: relationshipsAndGuarantorsReducer,
  pcsInformation: pcsInformationReducer,
  specialReports: specialReportsReducer,
  authorizationInformation: authorizationInformationReducer,
  cmsInformation: cmsInformationReducer,
  paymentInformation: paymentInformationReducer,
  apiErrorHandler: apiErrorHandlerReducer,
  patientSearch: patientSearchReducer,
  ekgImport: ekgImportReducer,
  pulsara: pulsaraReducer,
  qaAddendum: qaAddendumReducer,
  notificationCenter: notificationCenterReducer,
  patientImmunizations: patientImmunizationsReducer,
  clientData: clientDataReducer,
  cameraAttachments: cameraAttachmentsReducer,
  customFormSignature: customFormsSignatureReducer,
  factorsAffectingCareState: factorsAffectingCareReducer,
  sceneDescription: sceneDescriptionReducer,
  exposure: exposureReducer,
  titrate: titrateReducer,
  zollSupportTicket: zollSupportTicketReducer,
  initialAssessmentAirway: initialAssessmentsAirwayReducer,
  respiratory: RespiratoryReducer,
  neuro: neuroReducer,
  motorSensoryAssessment: motorSensoryAssessmentReducer,
  pupils: pupilsReducer,
  initialAssessment: initialAssessmentReducer,
  wasPatientImmobilized: wasPatientImmobilizedReducer,
  glassgowCommaScore: glassgowCommaScoreReducer,
  pediatricTraumaScore: pediatricTraumaScoreReducer,
  revisedTraumaScore: revisedTraumaScoreReducer,
  pulses: pulsesReducer,
  initialAssessmentCardio: cardioReducer,
  drain: drainReducer,
  labs: labsReducer,
  pnProcedure: pnProcedureReducer,
  medicationAdministeredStateListPNNVs: pnMedicationReducer,
  motorVehicleIncident: motorVehicleIncidentReducer,
  rules: rulesReducer,
  diagnosis: diagnosisReducer,
  __comments: commentsReducer,
  additionalAssessments: additionalAssessmentsReducer,
  bodyAssessment: bodyAssessmentReducer,
  drainsAndTubes: drainsAndTubesReducer,
  obstetrics: obstetricsReducer,
  burns: burnsReducer,
  bodySurvey: bodySurveyReducer,
  defaults: defaultsReducer,
  pnnv: pnnvReducer,
  locationSearch: locationSearchReducer,
  globalData: globalDataReducer,
  documentScanner: documentScannerReducer,
  customDisplayData: stateCustomDisplayData,
  completeChart: completeChartReducer,
  signerData: signerReducer,
  bloodProductAdministration: bloodProductAdministrationReducer,
  ventricularAssistDevice: ventricularAssistDeviceReducer,
  assessment: assessmentReducer,
  fillablePDFs: fillablePDFsReducer,
  pcrBackup: pcrBackupReducer,
})

export const initialState = {
  user: initialUserState,
  timeline: initialTimelineState,
  demo: initialDemoState,
  home: initialHomeState,
  vitals: initialVitalsState,
  login: initialLoginState,
  alert: initialAlertState,
  toast: initialToastState,
  loader: initialLoaderState,
  sessionPersist: initialSessionPersistState,
  sessionResume: initialSessionResumeState,
  pcr: initialPCRState,
  cookie: initialCookieState,
  crew: initialCrewState,
  times: initialTimesState,
  scrollToTop: initialScrollToTopState,
  cardiacArrest: initialCardiacArrestState,
  services: initialServiceState,
  patientInformation: initialPatientInformationState,
  patientDemographics: initialPatientDemographicsState,
  employer: initialEmployerState,
  patientAllergies: initialPatientAllergiesState,
  patientMedications: initialPatientMedicationsState,
  validation: initialValidationState,
  search: initialSearchState,
  dlScanner: initialDriversLicenseScannerState,
  patientMedicalHistory: initialPatientMedicalHistoryState,
  singleSelectPage: initialSingleSelectPageState,
  cardiac: initialCardiacState,
  addMedicationAdministered: initialAddMedicationAdministeredState,
  quickActions: initialQuickActionsState,
  procedure: initialProcedureState,
  intubation: initialIntubationState,
  options: initialOptionsState,
  tripInformation: initialTripInformationState,
  pickupAddress: initialPickupAddressState,
  airway: initialAirwayState,
  imagingUltrasound: initialImagingUltrasoundState,
  childbirthOB: initialChildbirthOBState,
  initiateIv: initialInitiateIvState,
  immobilization: initialImmobilizationState,
  medicalConsult: initialMedicalConsultState,
  extrication: initialExtricationState,
  operations: initialOperationsState,
  rescue: initialRescueState,
  ventilator: initialVentilatorState,
  criticalCare: initialCriticalCareState,
  hospitalNotify: initialHospitalNotifyState,
  woundCare: initialWoundCareState,
  submitChart: initialSubmitChartState,
  signatures: initialSignaturesState,
  narrative: initialNarrativeState,
  cadImport: initialCadImportState,
  situation: initialSituationState,
  history: initialHistoryState,
  ccHpiCardiacArrest: initialCcHpiCardiacArrestState,
  ccHpiInjuryDetails: initialCcHpiInjuryDetailsState,
  fluids: initialFluidsState,
  zip: initialZipState,
  disposition: initialDispostionState,
  configurations: initialConfigurationsState,
  billing: initialBillingState,
  relationshipsAndGuarantors: initialRelationshipsAndGuarantorsState,
  insuranceInformation: initialInsuranceInformationState,
  pcsInformation: initialPcsInformationState,
  specialReports: initialSpecialReportsState,
  authorizationInformation: initialAuthorizationInformationState,
  cmsInformation: initialCmsInformationState,
  paymentInformation: initialPaymentInformationState,
  apiErrorHandler: initialApiErrorHandlerState,
  patientSearch: initialPatientSearchState,
  ekgImport: initialEKGImportState,
  pulsara: initialPulsaraState,
  qaAddendum: initialQaAddendumState,
  notificationCenter: initialNotificationCenterState,
  patientImmunizations: initialPatientImmunizationsState,
  clientData: initialclientDataState,
  cameraAttachments: initialCameraAttachmentsState,
  customFormSignature: initialCustomFormsState,
  factorsAffectingCareState: initialFactorsAffectingCareState,
  sceneDescription: initialSceneDescriptionState,
  exposure: initialExposureState,
  titrate: initialTitrateState,
  zollSupportTicket: initialZollSupportTicketState,
  initialAssessment: initialAssessmentState,
  initialAssessmentAirway: initialAssessmentsAirwayState,
  respiratory: respiratoryState,
  neuro: initialNeuroState,
  motorSensoryAssessment: initialMotorSensoryAssessmentState,
  pupils: initialPupilsState,
  wasPatientImmobilized: initialWasPatientImmobilizedState,
  glassgowCommaScore: initialGlassgowCommaScoreState,
  pediatricTraumaScore: initialPediatricTraumaScoreState,
  revisedTraumaScore: initialRevisedTraumaScoreState,
  pulses: initialPulsesState,
  initialAssessmentCardio: initialCardioState,
  drain: initialDrainState,
  labs: initialLabsState,
  pnProcedure: initialPnProcedureState,
  medicationAdministeredStateListPNNVs: initialPnMedicationState,
  motorVehicleIncident: initialMotorVehicleIncidentState,
  rules: initialRulesState,
  diagnosis: initialDiagnosisState,
  __comments: initialCommentsState,
  additionalAssessments: initialAdditionalAssessmentsState,
  bodyAssessment: initialBodyAssessmentState,
  drainsAndTubes: initialDrainsAndTubesState,
  obstetrics: initialObstetricsState,
  burns: initialBurnsState,
  bodySurvey: initialBodySurveyState,
  defaults: initialDefaultsState,
  pnnv: initialPNNVState,
  locationSearch: initialLocationSearchState,
  globalData: initialGlobalDataState,
  documentScanner: initialDocumentScannerState,
  customDisplayData: initialCustomDisplayDataState,
  completeChart: initialCompleteChartState,
  signerData: initialSignerState,
  bloodProductAdministration: initialBloodProductAdministrationState,
  ventricularAssistDevice: initialVentricularAssistDeviceState,
  assessment: initialAssessmentProcedureState,
  fillablePDFs: initialFillablePDFsState,
  pcrBackup: initialPcrBackupState,
}

const mainReducer = (state: IRootState = initialState, action: any) => rootReducer(state, action)

const MAX_STATE_SIZE = Number(process.env.REACT_APP_REDUX_DEVTOOLS_MAX_STATE_SIZE) || StoreConstants.MAX_STATE_SIZE
const MAX_ACTION_SIZE = Number(process.env.REACT_APP_REDUX_DEVTOOLS_MAX_ACTION_SIZE) || StoreConstants.MAX_ACTION_SIZE

const sanitizeValue = (val: any) => {
  if (val instanceof Blob || val instanceof File) {
    return '<<BLOB>>'
  }

  if (typeof val === 'string' && /^data:.*;base64,/.test(val)) {
    return '<<BASE64>>'
  }

  if (typeof val === 'function') {
    return '<<FUNCTION>>'
  }

  if (typeof ImageData !== 'undefined' && val instanceof ImageData) {
    return '<<IMAGEDATA>>'
  }

  return val
}

const hydrateState = (middleware: any): Store => {
  const actionSanitizer = (action: any): any => {
    const { type, ...rest } = action
    const str = JSON.stringify(action)

    try {
      if (str.length < MAX_ACTION_SIZE) {
        // always sanitize some types e.g. blob
        // Object.fromEntries(...) will sanitize every payload object apart from "type"
        const sanitizedRest: Record<string, unknown> = Object.fromEntries(
          Object.entries(rest).map(([k, v]) => [k, sanitizeValue(v)])
        )

        return { type, ...sanitizedRest }
      }

      const sanitizedLargeActionPayload: Record<string, unknown> = Object.fromEntries(
        Object.keys(rest).map((key) => [key, `<<ACTION_TOO_LARGE size=${str.length}>>`])
      )

      return { type, ...sanitizedLargeActionPayload }
    } catch {
      const sanitizedErrorActionPayload: Record<string, unknown> = Object.fromEntries(
        Object.keys(rest).map((key) => [key, '<<ACTION_SERIALIZATION_ERROR>>'])
      )

      return { type, ...sanitizedErrorActionPayload }
    }
  }

  const stateSanitizer = (state: any): any => {
    const sanitized: Record<string, unknown> = {}

    for (const key of Object.keys(state)) {
      try {
        const stateValue = state[key]
        const forcedSanitized = sanitizeValue(stateValue)

        if (forcedSanitized !== stateValue) {
          sanitized[key] = forcedSanitized
          continue
        }

        const str = JSON.stringify(stateValue)
        if (str.length < MAX_STATE_SIZE) {
          continue
        }

        sanitized[key] = `<<STATE_TOO_LARGE size=${str.length}>>`
      } catch {
        sanitized[key] = '<<STATE_SERIALIZATION_ERROR>>'
      }
    }

    return { ...state, ...sanitized }
  }

  const devToolsOptions = {
    maxAge: Number(process.env.REACT_APP_REDUX_DEVTOOLS_MAX_AGE) || StoreConstants.MAX_AGE,
    actionSanitizer,
    stateSanitizer,
  }

  const devTools = composeWithDevTools(devToolsOptions)
  const composeEnhancers = process.env.REACT_APP_REDUX_DEVTOOLS_DISABLED !== 'true' ? devTools : compose

  return createStore(mainReducer, composeEnhancers(applyMiddleware(...middleware)))
}

const getStore = (): Store => {
  const sagaMiddleware = createSagaMiddleware()

  const middleware = [
    sagaMiddleware,
    exceptionHandlerMiddleware,
    reducerLoggerMiddleware,
    rulesMiddleware,
    // Note: This MUST be the last item in this array.
    // zLogRocket.utilities.reduxMiddleware,
    // !!!! Don't add anything below here !!!!
  ]

  const store = hydrateState(middleware)
  initSagas(sagaMiddleware)

  return store
}

export default getStore
